import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const ctaCards = [
    {
      icon: 'Users',
      title: t('cta.forPlayers'),
      description: t('cta.forPlayersDesc'),
      action: t('cta.applyNow'),
      path: '/player-registration-form',
      gradient: 'from-blue-600 to-blue-700',
      bgGradient: 'from-blue-50 to-blue-100',
      iconBg: 'from-blue-600 to-blue-700'
    },
    {
      icon: 'Building',
      title: t('cta.forClubs'),
      description: t('cta.forClubsDesc'),
      action: t('cta.browseTalent'),
      path: '/player-profiles',
      gradient: 'from-emerald-600 to-emerald-700',
      bgGradient: 'from-emerald-50 to-emerald-100',
      iconBg: 'from-emerald-600 to-emerald-700'
    },
    {
      icon: 'Handshake',
      title: t('cta.forPartners'),
      description: t('cta.forPartnersDesc'),
      action: t('cta.partnerWithUs'),
      path: '/contact',
      gradient: 'from-purple-600 to-purple-700',
      bgGradient: 'from-purple-50 to-purple-100',
      iconBg: 'from-purple-600 to-purple-700'
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full mb-6 shadow-sm">
            <Icon name="Rocket" size={18} className="text-blue-600" />
            <span className="text-sm font-bold text-slate-800">{t('cta.getStartedToday')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-emerald-600 to-blue-600 bg-clip-text text-transparent">
              {t('cta.readyNextStep')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('cta.joinThousands')}
          </p>
        </div>

        {/* CTA Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16">
          {ctaCards?.map((card, index) => (
            <div
              key={index}
              className="group relative bg-white border-2 border-slate-200 rounded-3xl p-8 md:p-10 text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden">
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${card?.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
              
              {/* Icon Container */}
              <div className="relative mb-8">
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br ${card?.iconBg} flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                  <Icon name={card?.icon} size={40} color="white" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="relative text-2xl md:text-3xl font-black text-slate-900 mb-4">{card?.title}</h3>
              
              {/* Description */}
              <p className="relative text-base md:text-lg text-slate-600 mb-8 leading-relaxed">{card?.description}</p>
              
              {/* CTA Button */}
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate(card?.path)}
                className={`relative bg-gradient-to-r ${card?.gradient} hover:opacity-90 text-white shadow-xl border-0 text-base font-bold w-full py-6`}>
                {card?.action}
              </Button>
            </div>
          ))}
        </div>

        {/* Bottom Contact Section */}
        <div className="text-center">
          <p className="text-base md:text-lg text-slate-600 mb-6 font-medium">
            {t('cta.contactUs')}
          </p>
          <Button
            variant="outline"
            size="lg"
            iconName="MessageSquare"
            iconPosition="left"
            onClick={() => navigate('/contact')}
            className="border-2 border-slate-300 text-slate-900 hover:bg-slate-100 font-bold px-10 py-6">
            {t('cta.contactUs')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CTASection;